/* eslint-disable @typescript-eslint/no-explicit-any */
import { CreatableSelect } from "chakra-react-select";
import { Params } from "custom-types";
import { useFormikContext } from "formik";
import {
  filter,
  find,
  flatMap,
  get,
  identity,
  includes,
  isEmpty,
  isEqual,
  map,
  uniq,
} from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  SelectField,
  SelectFieldOptions,
  SelectFieldProps,
  SelectGroupOption,
  SelectOption,
} from "../components";

export interface FormSelectFieldProps
  extends Omit<SelectFieldProps<any>, "onChange" | "label"> {
  name: string;
  loadOptions?: (
    inputValue: string,
    queryParam?: Params,
  ) => Promise<SelectFieldOptions<unknown>> | void;
  isLoading?: boolean;
  onInputChange?: (value: unknown) => void;
  /** Required dependencies: every listed field must have a value before this select is enabled or loads options. */
  fieldValuesToIncludeInQueryParam?: string[];
  /** Optional dependencies: merged into the loadOptions query when set, but never gate/disable the select. */
  optionalFieldValuesToIncludeInQueryParam?: string[];
  removeFieldNameFromQueryParam?: boolean;
  /** When true renders a CreatableSelect so users can type arbitrary values (e.g. email addresses) and press Enter. Requires isMulti. Value shape stays string[]. */
  isCreatable?: boolean;
  /**
   * Per-part chakra-react-select style overrides, as plain objects only —
   * no functions. chakra-react-select's `chakraStyles` API requires a
   * `(provided, state) => {}` function per part, but this prop (and the
   * config layers that feed it, e.g. tenant page configs) may pass through
   * a JSON-serialization boundary, which silently drops function values.
   * Express interactive states with Chakra's pseudo-style keys instead
   * (`_hover`, `_focusWithin`, `_selected`, ...) — those are just static
   * object keys and survive JSON fine. The actual `(provided, state) => {}`
   * wrapper functions chakra-react-select needs are built once, here in
   * this component, merging `provided` with these plain values.
   */
  selectChakraStyles?: Record<string, Record<string, unknown>>;
}

function AsyncSelectField({
  loadOptions,
  name,
  fieldValuesToIncludeInQueryParam,
  optionalFieldValuesToIncludeInQueryParam,
  removeFieldNameFromQueryParam,
  isLoading,
  ...props
}: Omit<FormSelectFieldProps, "options" | "loadOptions"> & {
  loadOptions: (
    inputValue: string,
    queryParam?: Params,
  ) => Promise<SelectFieldOptions<unknown>> | void;
}) {
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [options, setOptions] = useState<SelectFieldOptions<any>>([]);
  const [loadOptionsQueryParams, setLoadOptionsQueryParams] = useState<Params>(
    {},
  );

  const isApiCalled = useRef(false);
  const { values } = useFormikContext();

  const value = useMemo(
    () =>
      props.isMulti
        ? filter(options, (item: any) => {
            const fieldValue = get(values, name, []) || [];
            return includes(fieldValue, item.value);
          })
        : find(options, { value: get(values, name) }),
    [props, options, name, values],
  );

  const hasValue = useMemo(() => {
    let fieldHasValue = true;
    const query: Params = {};
    fieldValuesToIncludeInQueryParam?.forEach((key) => {
      const fieldVal = get(values, key, "");
      query[key] = fieldVal;
      if (fieldVal === undefined || fieldVal === null || !fieldVal.toString()) {
        fieldHasValue = false;
      }
    });
    // Optional dependencies narrow the options when set but must never gate the
    // field, so they are folded into the same query object without touching
    // `fieldHasValue` — and omitted entirely while empty so the request keeps
    // its unfiltered shape.
    optionalFieldValuesToIncludeInQueryParam?.forEach((key) => {
      const fieldVal = get(values, key, "");
      if (fieldVal !== undefined && fieldVal !== null && fieldVal.toString()) {
        query[key] = fieldVal;
      }
    });
    if (!isEqual(query, loadOptionsQueryParams)) {
      setLoadOptionsQueryParams(query);
    }
    return fieldHasValue;
  }, [
    fieldValuesToIncludeInQueryParam,
    optionalFieldValuesToIncludeInQueryParam,
    values,
    loadOptionsQueryParams,
  ]);

  useEffect(() => {
    const init = async () => {
      setIsLoadingOptions(true);
      setOptions([]);
      const queryParams = removeFieldNameFromQueryParam
        ? {}
        : loadOptionsQueryParams;

      const loadedOptions = (await loadOptions(
        "",
        queryParams,
      )) as SelectFieldOptions<unknown>;
      isApiCalled.current = true;
      setOptions(loadedOptions);
      setIsLoadingOptions(false);
    };

    if (isEmpty(fieldValuesToIncludeInQueryParam)) {
      // With only optional dependencies there is nothing to wait for, but the
      // list still has to reload when one of them changes — so the
      // once-only `isApiCalled` guard can't apply here.
      if (
        !isApiCalled.current ||
        !isEmpty(optionalFieldValuesToIncludeInQueryParam)
      ) {
        init();
      }
    } else if (hasValue) {
      init();
    }

    return () => {
      isApiCalled.current = true;
    };
  }, [
    loadOptionsQueryParams,
    hasValue,
    fieldValuesToIncludeInQueryParam,
    optionalFieldValuesToIncludeInQueryParam,
    loadOptions,
    removeFieldNameFromQueryParam,
  ]);

  return (
    <SelectField
      {...props}
      isDisabled={
        props.isDisabled ||
        (!hasValue && !isEmpty(fieldValuesToIncludeInQueryParam))
      }
      value={value as any}
      options={options}
      isLoading={isLoadingOptions || isLoading}
    />
  );
}

export function FormSelectField({
  options,
  name,
  loadOptions,
  onInputChange,
  isCreatable,
  selectChakraStyles,
  ...props
}: FormSelectFieldProps) {
  const { values, setFieldValue, setFieldTouched } = useFormikContext();
  const fieldOptions = useMemo(() => options, [options]);

  const getSelectFieldValue = useMemo(() => {
    if (isCreatable && props.isMulti) {
      // Creatable fields store plain strings; synthesise option objects so chips render correctly.
      const fieldValue: string[] = (get(values, name, []) as string[]) || [];
      return fieldValue.map((v) => ({ label: v, value: v }));
    }
    const initialSelectedValues =
      props.selectType === "checkbox-group"
        ? flatMap(fieldOptions, (item: SelectGroupOption<any>) => {
            const tempOptions = map(
              item.options,
              (option: SelectOption<unknown>) => {
                const fieldValue = get(values, name, []);
                if ((fieldValue as any[]).includes(option.value)) {
                  return { label: option.label, value: option.value };
                }
              },
            ).filter(identity);
            return tempOptions;
          })
        : props.isMulti
          ? filter(fieldOptions, (item: any) => {
              const fieldValue = get(values, name, []) || [];
              return includes(fieldValue, item.value);
            })
          : find(fieldOptions, { value: get(values, name) });
    return initialSelectedValues;
  }, [fieldOptions, isCreatable, name, props, values]);

  const commonProps: any = useMemo(() => {
    return {
      useBasicStyles: true,
      name,
      isClearable: true,
      menuPortalTarget: document.body,
      // Kept as the default look for any consumer that doesn't pass its own
      // selectChakraStyles. Note: chakra-react-select's own custom
      // components (Control, Menu, MenuList, Option, ...) read `chakraStyles`
      // below, not this — `menuPortal` is the exception, since it's the one
      // part still rendered by react-select's own core, which does honor
      // `styles`.
      styles: {
        menuPortal: (provided: any) => ({ ...provided, zIndex: 100 }),
        control: (provided: any, state: any) => ({
          ...provided,
          borderRadius: "9px",
          borderColor: state.isFocused ? "#3A4BD8" : "#DDE1E6",
          boxShadow: state.isFocused
            ? "0 0 0 3px rgba(58,75,216,0.12)"
            : "none",
          fontSize: "13.5px",
          minHeight: "40px",
          "&:hover": { borderColor: state.isFocused ? "#3A4BD8" : "#C7CCD4" },
        }),
        menu: (provided: any) => ({
          ...provided,
          borderRadius: "10px",
          boxShadow: "0 12px 28px rgba(16,22,35,0.13)",
          padding: "5px",
          border: "1px solid #E5E8EC",
        }),
        option: (provided: any, state: any) => ({
          ...provided,
          borderRadius: "7px",
          fontSize: "13px",
          padding: "9px 13px",
          backgroundColor: state.isSelected
            ? "#3A4BD8"
            : state.isFocused
              ? "#F0F1FE"
              : "transparent",
          color: state.isSelected ? "#fff" : "#3A4150",
          cursor: "pointer",
        }),
        placeholder: (provided: any) => ({
          ...provided,
          color: "#9AA2AE",
          fontSize: "13.5px",
        }),
        singleValue: (provided: any) => ({
          ...provided,
          color: "#14181F",
          fontSize: "13.5px",
        }),
        multiValue: (provided: any) => ({
          ...provided,
          backgroundColor: "#F0F1FE",
          borderRadius: "7px",
        }),
        multiValueLabel: (provided: any) => ({
          ...provided,
          color: "#3A4BD8",
          fontSize: "12.5px",
          fontWeight: 500,
        }),
        multiValueRemove: (provided: any) => ({
          ...provided,
          color: "#8C97E8",
          borderRadius: "0 7px 7px 0",
          "&:hover": { backgroundColor: "#E0E3FF", color: "#3A4BD8" },
        }),
      },
      // Real per-part functions chakra-react-select requires, each just
      // merging its base `provided` style with the plain, JSON-safe
      // overrides passed in via `selectChakraStyles` (see prop doc above).
      chakraStyles: {
        control: (provided: any) => ({
          ...provided,
          ...selectChakraStyles?.control,
        }),
        menu: (provided: any) => ({
          ...provided,
          ...selectChakraStyles?.menu,
        }),
        menuList: (provided: any) => ({
          ...provided,
          ...selectChakraStyles?.menuList,
        }),
        option: (provided: any) => ({
          ...provided,
          ...selectChakraStyles?.option,
        }),
        placeholder: (provided: any) => ({
          ...provided,
          ...selectChakraStyles?.placeholder,
        }),
        singleValue: (provided: any) => ({
          ...provided,
          ...selectChakraStyles?.singleValue,
        }),
        dropdownIndicator: (provided: any) => ({
          ...provided,
          ...selectChakraStyles?.dropdownIndicator,
        }),
        multiValue: (provided: any) => ({
          ...provided,
          ...selectChakraStyles?.multiValue,
        }),
        multiValueLabel: (provided: any) => ({
          ...provided,
          ...selectChakraStyles?.multiValueLabel,
        }),
        multiValueRemove: (provided: any) => ({
          ...provided,
          ...selectChakraStyles?.multiValueRemove,
        }),
      },
      menuPlacement: "auto",
      menuPosition: "fixed",
      colorScheme: "primary",
      focusBorderColor: "#3A4BD8",
      selectedOptionColorScheme: "primary",
      placeholder: "Select Option...",
      closeMenuOnSelect: true,
      size: "md",
      ...props,
      onBlur: () => {
        setFieldTouched(name, true);
      },
      onChange: (newVal: SelectOption<any>) => {
        if (props.isMulti || props.selectType === "checkbox-group") {
          setFieldValue(name, uniq(map(newVal, (item) => item.value)));
        } else {
          setFieldValue?.(name, get(newVal, "value", ""));
        }
        if (onInputChange) onInputChange(newVal);
      },
    };
  }, [
    name,
    props,
    setFieldValue,
    onInputChange,
    setFieldTouched,
    selectChakraStyles,
  ]);

  if (isCreatable) {
    return (
      <CreatableSelect
        {...commonProps}
        value={getSelectFieldValue}
        options={fieldOptions ?? []}
        // New options created by typing are passed through onChange as-is; value === label for created entries.
        getNewOptionData={(inputValue: string) => ({
          label: inputValue,
          value: inputValue,
        })}
      />
    );
  }

  return loadOptions ? (
    <AsyncSelectField
      openMenuOnFocus={props.onBlur}
      loadOptions={loadOptions}
      customOptions={fieldOptions}
      {...commonProps}
    />
  ) : (
    <SelectField
      {...commonProps}
      width="100%"
      openMenuOnFocus={props.onBlur}
      value={getSelectFieldValue}
      options={fieldOptions}
    />
  );
}

export default React.memo(FormSelectField);
